import { LOG_MESSAGES } from '@/constants/logMessages';
import {
  useApiMutation,
  useApiMutationWithBaseUrl,
  useApiQuery,
  useApiStream,
} from '@/hooks/useApi';
import { useChatStore } from '@/hooks/useChatStore';
import useConversationHistory from '@/hooks/useConversationHistory';
import { useProfile } from '@/hooks/useProfile';
import type {
  ChatHistoryResponse,
  Conversation,
  CreateStreamMessagePayload,
  Message,
  ParsedStreamResponse,
} from '@/interface/chat';
import ChatGreeting from '@/pages/chat/components/ChatGreeting';
import ChatHistory from '@/pages/chat/components/ChatHistory';
import ChatInputContainer from '@/pages/chat/components/ChatInputContainer';
import ExploreLayout from '@/pages/explore/ExploreLayout';
import { logEvent } from '@/util/logger';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';

import VoiceModeContainer from '@/components/chat/VoiceModeContainer';
import AnnouncementBanners from '@/components/layout/announcementBanner/announcementBanners';
import { hasValidSummary } from '@/components/layout/sidebar/recentChatContainer';
import { isDemoEnvironment } from '@/util/demoUtils';
import { logRocketUtil } from '@/util/logrocket';
import {
  isStaffMode,
  generateStaffConversationId,
  isStaffConversationId,
} from '@/util/staffUtils';
import ChatHistoryLoader from './components/ChatHistoryLoader';
import ChatSuggestions from './components/ChatSuggestions';
import { queryTableNameUtil } from '@/util/queryTableNameUtil';

type props = {
  type?: 'Performance' | 'Marketing' | 'Transactions';
  chatId: string;
};
const ChatPage: React.FC<props> = ({ type, chatId }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isStaffUser = isStaffMode();
  const isDemo = isDemoEnvironment();

  const [input, setInput] = useState('');
  const {
    voiceMode,
    setChatHistory,
    setChatId,
    setError,
    addMessageRaw,
    updateMessageRaw,
    hasUnprocessedMessage,
    chatHistory,
    unprocessedMessage,
    currentPage,
    showTypingIndicator,
    isStreaming,
    streamingText,

    updateMessageIdRaw,
    setHasUnprocessedMessage,
    setUnprocessedMessage,
    setShowTypingIndicator,
    setIsStreaming,
    setStreamingText,
    setConversationStreamingState,
    isProcessingMessage: isProcessingMessageStore,
    setIsProcessingMessage: storeSetIsProcessingMessage,
    setTotalPages: storeSetTotalPages,
  } = useChatStore();
  const navigate = useNavigate();
  const typingIndicatorRef = useRef(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [showMaxCharTooltip, setShowMaxCharTooltip] = useState(false);
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(
    !!chatHistory.length,
  );
  const silenceTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isProcessingMessage = useRef(false);
  const messageProcessed = useRef(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });

  const showChatLayout = !!chatId || isCreatingChat;
  const { t } = useTranslation(['chat']);
  const maxCharLength = 4096;

  const { profile } = useProfile();
  const expSystemKey = profile?.value[0].eXpSystemKey!;
  const username =
    profile?.value[0].MemberFirstName + ' ' + profile?.value[0].MemberLastName;

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  const { addOrUpdateConversation, sortedChats } = useConversationHistory();

  const { streamData } =
    useApiStream<CreateStreamMessagePayload>('/chat_stream');

  const { mutateAsync: createMessage } = useApiMutation({
    url: '/user/createMessage',
    method: 'post',
  });
  const { mutateAsync: createStreamMessage } = useApiMutationWithBaseUrl({
    baseURL: process.env.REACT_APP_ORCHESTRATOR_API_URL || '',
    url: '/user/createStreamMessage',
    method: 'post',
  });

  const { mutateAsync: updateMessageApi } = useApiMutation({
    url: '/user/updateMessage',
    method: 'put',
  });

  const {
    data: chatHistoryData,
    isError: chatHistoryError,
    isFetched: chatHistoryFetched,
    isPending: chatHistoryPending,
    isFetching: chatHistoryFetching,
    isLoading: chatHistoryLoading,
  } = useApiQuery<ChatHistoryResponse>(
    [
      '/user/fetchAllMessage',
      profile?.value[0].eXpMember.Uuid as string,
      chatId as string,
    ],
    '/user/fetchAllMessage',
    {
      enabled:
        !isDemo &&
        chatId !== undefined &&
        !isStaffConversationId(chatId) &&
        !hasUnprocessedMessage &&
        messageProcessed.current,
      refetchOnWindowFocus: false,
    },
    false,
    {
      userId: profile?.value[0].eXpMember.Uuid as string,
      conversationId: chatId,
      pageNumber: currentPage,
      pageSize: 10,
    },
  );

  const { data } = useApiQuery<any>(
    ['/user/prompts', "What's my eXp pulse?"],
    '/user/prompts',
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    false,
    {
      question: "What's my eXp pulse?",
      isActive: 'true',
    },
  );

  useEffect(() => {
    if (isDemo && chatId && !isCreatingChat && chatHistory.length === 0) {
      navigate('/chat', { replace: true });
    }
  }, [isDemo, chatId, isCreatingChat, chatHistory.length, navigate]);

  // Staff mode: if a staff-generated chatId is present but Redux state is empty
  // (e.g. after a page refresh), redirect to /chat since those messages are gone.
  useEffect(() => {
    if (
      isStaffConversationId(chatId) &&
      !isCreatingChat &&
      chatHistory.length === 0
    ) {
      navigate('/chat', { replace: true });
    }
  }, [chatId, isCreatingChat, chatHistory.length, navigate]);

  const startTranscription = async () => {
    if (!browserSupportsSpeechRecognition || !isMicrophoneAvailable) {
      setSnackbar({
        open: true,
        message: "We couldn't process your speech. Please try again.",
      });
      return;
    }

    resetTranscript();
    await SpeechRecognition.startListening({ continuous: true });
  };

  const stopTranscription = async () => {
    await SpeechRecognition.stopListening();
    if (silenceTimeout.current) {
      clearTimeout(silenceTimeout.current);
      silenceTimeout.current = null;
    }
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value.length <= maxCharLength) {
        setInput(value);
        setShowMaxCharTooltip(false);
      } else {
        setInput(value.substring(0, maxCharLength));
        setShowMaxCharTooltip(true);
        setTimeout(() => {
          setShowMaxCharTooltip(false);
        }, 3000);
      }
    },
    [maxCharLength],
  );

  const handleStream = async (
    payload: CreateStreamMessagePayload,
    newMessage: Message,
    chatId: string,
    options?: { persistMode?: 'update' | 'create' },
  ) => {
    const persistMode = options?.persistMode ?? 'update';
    let currentText = '';
    const startTime = Date.now();
    // Create a new abort controller for this stream
    abortControllerRef.current = new AbortController();
    try {
      await streamData(
        payload,
        // on chunk update
        (chunk) => {
          // Check if the stream was aborted
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Stream was aborted');
          }

          // Hide typing indicator on first chunk
          if (typingIndicatorRef.current) {
            setConversationStreamingState({
              conversationId: chatId,
              showTypingIndicator: false,
            });
            typingIndicatorRef.current = false;
          }

          // Update streaming text and message
          currentText += chunk;
          setConversationStreamingState({
            conversationId: chatId,
            streamingText: currentText,
            isStreaming: true,
          });
          updateMessageRaw({
            ...newMessage,
            conversationId: chatId,
            response: currentText,
            createdOn: newMessage.createdOn,
          });
        },
        // on stream complete
        async (
          messageId,
          deepLink,
          parsedResponse: ParsedStreamResponse | null,
        ) => {
          const finalText = currentText;
          const endTime = Date.now();
          const responseTime = endTime - startTime;

          logRocketUtil.trackChatResponse({
            responseTime,
            messageId: messageId as string,
            messageLength: finalText.length,
            conversationId: chatId!,
          });

          updateMessageRaw({
            ...newMessage,
            conversationId: chatId,
            response: finalText,
            uId: messageId as string,
            deepLink: deepLink || '',
            sql_query: parsedResponse?.sql_query || '',
            table_used: parsedResponse?.table_used || '',
            rewritten_query: parsedResponse?.rewritten_query || null,
            sql_result: parsedResponse?.sql_result || null,
            documents: parsedResponse?.documents,
            createdOn: newMessage.createdOn,
            is_graph: parsedResponse?.is_graph || false,
          });

          // Cleanup states
          setConversationStreamingState({
            conversationId: chatId,
            isStreaming: false,
            streamingText: '',
            showTypingIndicator: false,
            isProcessingMessage: false,
            unprocessedMessage: null,
          });
          typingIndicatorRef.current = false;
          resetTranscript();
          abortControllerRef.current = null;
          setIsCreatingChat(false);

          // In staff mode, skip all backend persistence — data lives only in Redux
          if (isStaffUser) {
            // Only update summary for staff-created conversations (dummy IDs).
            // For existing DB conversations, don't touch summary or position.
            if (parsedResponse && isStaffConversationId(chatId)) {
              addOrUpdateConversation({
                uId: chatId,
                converstionSummary:
                  parsedResponse.conversation_summary ||
                  newMessage.messageContent,
                updatedOn: new Date().toISOString(),
                createdOn: new Date().toISOString(),
                expireOn: new Date(
                  Date.now() + 30 * 24 * 60 * 60 * 1000,
                ).toISOString(),
                userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
              });
            }
          } else {
            // Persist the message in the backend after streaming completes
            try {
              if (parsedResponse) {
                const updateMessagePayload: any = {
                  uId:
                    newMessage.messageId ||
                    newMessage.uId ||
                    (messageId as string) ||
                    '',
                  userId:
                    parsedResponse.userId ||
                    profile?.value[0].eXpMember.Uuid ||
                    '',
                  conversationId: parsedResponse.conversationId || chatId || '',
                  messageType: parsedResponse.messageType || 'text',
                  deviceType: parsedResponse.deviceType || 'desktop',
                  isUpdatedConversation:
                    parsedResponse.isUpdatedConversation ??
                    !parsedResponse.is_first_message,
                  username: parsedResponse.username || username || '',
                  chatType: parsedResponse.chatType || 'Text Mode',
                  is_first_message: parsedResponse.is_first_message || false,
                  answer: parsedResponse.answer || finalText,
                  conversation_summary:
                    parsedResponse.conversation_summary ||
                    newMessage.messageContent,
                  deeplink: parsedResponse.deeplink || deepLink || '',
                  sql_query: parsedResponse.sql_query || '',
                  table_used: parsedResponse.table_used || '',
                  sql_success: parsedResponse.sql_success || false,
                  sqlResult: parsedResponse.sql_result || null,
                  function_timings: parsedResponse.function_timings || {
                    generate_conversation_summary: 0,
                    similarity_search: 0,
                    generate_sql_query: 0,
                    execute_sql_query: 0,
                    generate_final_answer: 0,
                    answer_directly_with_rag: 0,
                  },
                  messageContent:
                    parsedResponse.messageContent || newMessage.messageContent,
                };

                // Include documents only if available
                if (
                  parsedResponse.documents &&
                  Array.isArray(parsedResponse.documents) &&
                  parsedResponse.documents.length > 0
                ) {
                  updateMessagePayload.documents = parsedResponse.documents;
                }

                const rewrittenQuery = parsedResponse?.rewritten_query?.trim();
                if (rewrittenQuery) {
                  updateMessagePayload.rewrittenQuery = rewrittenQuery;
                }

                if (persistMode === 'update') {
                  // New-conversation first message (already created via /user/createMessage)
                  const updatedMessage: any =
                    await updateMessageApi(updateMessagePayload);
                  updateMessageIdRaw({
                    ...newMessage,
                    messageId:
                      updatedMessage.data?.id || updatedMessage.data?.uId,
                    deepLink:
                      updatedMessage?.deepLink ||
                      updatedMessage.data?.deeplink ||
                      deepLink ||
                      '',
                    documents: parsedResponse?.documents,
                    rewritten_query: parsedResponse?.rewritten_query || null,
                  });
                  addOrUpdateConversation({
                    uId: chatId,
                    converstionSummary: parsedResponse.conversation_summary,
                    updatedOn: new Date().toISOString(),
                    createdOn: new Date().toISOString(),
                    expireOn: new Date(
                      Date.now() + 30 * 24 * 60 * 60 * 1000,
                    ).toISOString(),
                    userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
                  });
                } else {
                  const created: any =
                    await createStreamMessage(updateMessagePayload);

                  const createdMessage = created?.data;
                  if (createdMessage?.uId || createdMessage?.id) {
                    updateMessageIdRaw({
                      ...newMessage,
                      messageId: createdMessage.id || createdMessage.uId,
                      deepLink:
                        createdMessage.deepLink ||
                        createdMessage.deeplink ||
                        deepLink ||
                        '',
                      documents: parsedResponse?.documents,
                      rewritten_query: parsedResponse?.rewritten_query || null,
                    });
                  }
                }
              }
              // storeSetIsProcessingMessage(false);
              // setUnprocessedMessage(null);

              // Immediately trigger a refresh of the conversation list
              await queryClient.invalidateQueries({
                queryKey: [
                  '/user/getAllConversation',
                  profile?.value[0].eXpMember.Uuid as string,
                ],
              });
            } catch (error) {
              await queryClient.invalidateQueries({
                queryKey: [
                  '/user/getAllConversation',
                  profile?.value[0].eXpMember.Uuid as string,
                ],
              });
              setSnackbar({
                open: true,
                message: `Error: ${(error as Error).message}`,
              });
            }
          }
        },
        // on stream error
        (error) => {
          console.error('Stream error:', error);
          setConversationStreamingState({
            conversationId: chatId,
            isStreaming: false,
            streamingText: '',
            showTypingIndicator: false,
            isProcessingMessage: false,
            unprocessedMessage: null,
          });
          typingIndicatorRef.current = false;
          // storeSetIsProcessingMessage(false);
          // setUnprocessedMessage(null);
          updateMessageRaw({
            ...newMessage,
            conversationId: chatId,
            response: currentText,
            error: error.message,
          });

          setSnackbar({
            open: true,
            message: `Error: ${error.message}`,
          });

          abortControllerRef.current = null;
        },
        abortControllerRef.current?.signal,
      );
    } catch (error) {
      setConversationStreamingState({
        conversationId: chatId,
        isStreaming: false,
        streamingText: '',
        showTypingIndicator: false,
        isProcessingMessage: false,
        unprocessedMessage: null,
      });
      // storeSetIsProcessingMessage(false);
      // setUnprocessedMessage(null);
      abortControllerRef.current = null;

      // Don't show an error if the stream was intentionally aborted
      if ((error as Error).message === 'Stream was aborted') {
        return;
      }

      updateMessageRaw({
        ...newMessage,
        response: '',
        conversationId: chatId,
        error: (error as Error).message,
      });

      setSnackbar({
        open: true,
        message: `Error creating message: ${(error as Error).message}`,
      });
    }
  };

  const handleCreateConversation = async (newMessage: Message) => {
    try {
      setIsCreatingChat(true);

      // In staff mode, skip the backend API call and use a dummy conversation ID
      if (isStaffUser) {
        const dummyConversationId = generateStaffConversationId();
        setChatId(dummyConversationId);
        addOrUpdateConversation({
          uId: dummyConversationId,
          converstionSummary: newMessage.messageContent,
          updatedOn: new Date().toISOString(),
          createdOn: new Date().toISOString(),
          expireOn: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
        });
        return navigate(`/chat/${dummyConversationId}`);
      }

      const createMessagePayload: any = {
        userId: profile?.value[0].eXpMember.Uuid as string,
        userEmail: profile?.value[0].MemberEmail as string,
        userName: `${profile?.value[0].MemberFirstName} ${profile?.value[0].MemberLastName}`,
        messageContent: newMessage.messageContent,
        chatType: 'Text Mode',
        answer: 'In progress...',
        deeplink: '',
        table_used: '',
        sql_query: '',
        conversation_summary: newMessage.messageContent,
        sql_success: false,
        function_timings: {
          generate_conversation_summary: 0,
          similarity_search: 0,
          generate_sql_query: 0,
          execute_sql_query: 0,
          generate_final_answer: 0,
          answer_directly_with_rag: 0,
        },
      };

      const response: any = await createMessage(createMessagePayload);
      const createdMessage = response?.data;
      const newConversationId =
        createdMessage?.conversationInfo?.conversationId ||
        createdMessage?.conversationId;

      if (createdMessage?.uId || createdMessage?.id) {
        updateMessageIdRaw({
          ...newMessage,
          messageId: createdMessage.uId || createdMessage.id,
          deepLink: createdMessage.deepLink || createdMessage.deeplink || '',
        });
      }

      if (newConversationId) {
        setChatId(newConversationId);
        addOrUpdateConversation({
          uId: newConversationId,
          converstionSummary: '',
          updatedOn: new Date().toISOString(),
          createdOn: new Date().toISOString(),
          expireOn: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
        });
        // Navigate first before starting the stream
        return navigate(`/chat/${newConversationId}`);
      }
    } catch (error) {
      storeSetIsProcessingMessage(false);
      setSnackbar({
        open: true,
        message: `Error creating conversation: ${(error as Error).message}`,
      });
    }
  };

  const processMessage = async (newMessage: Message, isNew = true) => {
    try {
      let messageToProcess = newMessage;
      setShowTypingIndicator(true);
      typingIndicatorRef.current = true;
      setIsStreaming(true);
      storeSetIsProcessingMessage(true);
      if (isNew) {
        // Set the user message so they can see it immediately if it is a new message
        addMessageRaw({
          ...messageToProcess,
          isNew: chatId ? false : true,
        });
      }
      setChatHistoryLoaded(true);

      let conversationId = chatId;

      if (!conversationId) {
        // Create a conversation if needed and navigate to the conversation page
        return await handleCreateConversation(messageToProcess);
      }

      // Check if the current conversation has a missing summary
      const currentConversation = sortedChats?.find(
        (chat: Conversation) => chat.uId === conversationId,
      );
      const hasMissingSummary =
        currentConversation &&
        !hasValidSummary(currentConversation.converstionSummary);
      let isFallbackSummary = false;
      if (chatHistory.length > 0) {
        isFallbackSummary =
          currentConversation?.converstionSummary ===
          chatHistory[chatHistory.length - 1]?.messageContent;
      }

      // const isCanadaAgent = profile?.value[0].eXpSystemKey === 6;
      // Create message payload
      const messagePayload: CreateStreamMessagePayload = {
        uuid: profile?.value[0].eXpMember.Uuid as string,
        is_first_message: hasMissingSummary || !isNew || isFallbackSummary,
        question: messageToProcess.messageContent,
        username: username || '',
        memberkeynumeric: profile?.value[0].MemberKeyNumeric ?? 0,
        teamkeynumeric: profile?.value[0].TeamKeyNumeric ?? 0,
        province: profile?.value[0].MemberStateOrProvince,
        sql_query: messageToProcess.sql_query || '',
        table_used: messageToProcess.table_used || '',
        resource_type: messageToProcess.resource_type || '',
        sqlResult: messageToProcess.sql_result ?? undefined,
        team_lead: profile?.value[0].eXpMember?.TeamLead || false,
        thread_id: isStaffConversationId(conversationId) ? '' : conversationId,
        exp_system_key: profile?.value[0].eXpSystemKey!,
      };
      setInput('');
      setUnprocessedMessage({
        ...messageToProcess,
        conversationId,
      });
      // Get the stream response and start processing
      await handleStream(messagePayload, messageToProcess, conversationId, {
        persistMode: isNew ? 'create' : 'update',
      });
    } catch (error) {
      console.error('error', error);
      updateMessageRaw({
        ...newMessage,
        response: '',
        error: (error as Error).message,
      });

      setSnackbar({
        open: true,
        message: `Error processing message: ${(error as Error).message}`,
      });
    }
  };

  const handleCreateChat = async (message: string, suggestion?: any) => {
    try {
      const randomId = `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
      const newMessage: Message = {
        id: randomId,
        uId: randomId,
        messageContent: message,
        response: '',
        createdOn: new Date().toISOString(),
        updatedOn: new Date().toISOString(),
        deepLink: '',
        sql_query: suggestion?.sqlQuery || '',
        table_used: suggestion?.tableName || '',
        resource_type: suggestion?.resourceType || '',
        expiredOn: new Date(
          Date.now() + 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
        userId: profile?.value[0].eXpMember.Uuid || 'user-id',
        queryResponse: '',
      };

      await processMessage(newMessage);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error creating message: ${(error as Error).message}`,
      });
    }
  };

  const handlePulseClick = () => {
    const pulseData = data?.data[0];
    const { sqlQuery, tableName } = queryTableNameUtil(
      pulseData?.sqlQuery,
      pulseData?.tableName,
      expSystemKey,
    );
    const suggestion = {
      question: pulseData?.question,
      resourceType: pulseData?.resourceType,
      sqlQuery: sqlQuery,
      tableName: tableName,
    };
    handleSendMessage("What's my eXp pulse?", suggestion);
  };

  const handleSendMessage = useCallback(
    (message?: string, suggestion?: any) => {
      if (!chatHistoryLoaded && chatId) return;
      // Ensure message is always a string
      message = typeof message === 'string' ? message : input;

      // Prevent errors by ensuring message is a valid string
      if (!message || typeof message !== 'string' || message.trim() === '')
        return;

      if (isStreaming) {
        // If already streaming, abort the current stream before starting a new one
        // if (abortControllerRef.current) {
        //   abortControllerRef.current.abort();
        //   abortControllerRef.current = null;
        // }
        return;
      }

      logEvent(LOG_MESSAGES.USER_SENT_MESSAGE, {
        message,
        chatId,
      });

      setIsStreaming(true);
      setStreamingText('');
      setIsCreatingChat(true);

      handleCreateChat(message, suggestion);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chatHistoryLoaded,
      chatId,
      input,
      isStreaming,
      setIsStreaming,
      setStreamingText,
    ],
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      if (input.trim()) {
        handleSendMessage();
      }
    }
  };

  const closeSnackbar = () => {
    logEvent(LOG_MESSAGES.USER_CLOSED_SNACKBAR);
    setSnackbar({ open: false, message: '' });
  };

  useEffect(() => {
    handleInputChange({
      target: { value: transcript },
    } as React.ChangeEvent<HTMLInputElement>);
  }, [handleInputChange, transcript]);

  useEffect(() => {
    if (!input.trim()) {
      resetTranscript();
    }
  }, [input, resetTranscript]);

  useEffect(() => {
    if (
      chatId &&
      !chatHistoryLoaded &&
      chatHistoryFetched &&
      !chatHistoryPending &&
      !chatHistoryFetching &&
      !chatHistoryLoading
    ) {
      if (chatHistoryError) {
        setError('Failed to load chat history');
      } else if (chatHistoryData) {
        setChatHistory([...chatHistoryData.data.items, ...chatHistory]);
        storeSetTotalPages(chatHistoryData.data.totalPages);
        setChatHistoryLoaded(true);
      }
    }
  }, [
    chatId,
    chatHistory,
    chatHistoryLoaded,
    chatHistoryFetched,
    chatHistoryError,
    chatHistoryData,
    setChatHistory,
    chatHistoryPending,
    chatHistoryFetching,
    chatHistoryLoading,
    setError,
    storeSetTotalPages,
  ]);

  useEffect(() => {
    if (listening) {
      if (silenceTimeout.current) {
        clearTimeout(silenceTimeout.current);
      }

      const timeout = setTimeout(() => {
        stopTranscription();
        if (!transcript) {
          setSnackbar({
            open: true,
            message: t('listeningErrorMessage'),
          });
        }
      }, 5000);

      silenceTimeout.current = timeout;
    }

    return () => {
      if (silenceTimeout.current) {
        clearTimeout(silenceTimeout.current);
      }
    };
  }, [listening, t, transcript]);
  useEffect(() => {
    if (!chatId) return;
    if (chatId && hasUnprocessedMessage && !isProcessingMessage.current) {
      isProcessingMessage.current = true;
      setHasUnprocessedMessage(false);
      processMessage(chatHistory[0], false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, hasUnprocessedMessage, chatHistory]);

  useEffect(() => {
    if (!hasUnprocessedMessage && !isProcessingMessage.current) {
      messageProcessed.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatId,
    hasUnprocessedMessage,
    chatHistoryLoaded,
    chatHistoryFetched,
    chatHistoryPending,
    chatHistoryFetching,
    chatHistoryLoading,
    chatHistoryError,
    chatHistoryData,
  ]);
  useEffect(() => {
    if (
      unprocessedMessage !== null &&
      unprocessedMessage.conversationId === chatId &&
      isProcessingMessageStore
    ) {
      setIsStreaming(true);
      setShowTypingIndicator(true);
      setStreamingText('');
      updateMessageRaw({ ...unprocessedMessage });
    }
    if (
      unprocessedMessage !== null &&
      (isStreaming || showTypingIndicator) &&
      unprocessedMessage.conversationId !== chatId
    ) {
      setIsStreaming(false);
      setShowTypingIndicator(false);
      setStreamingText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  return (
    <Box
      data-testid="chat-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        key="snackbar"
        data-testid="chat-snackbar"
        sx={{
          backgroundColor: theme.palette.tooltips.main,
          color: theme.palette.text.primary,
        }}
      />
      {voiceMode ? (
        <VoiceModeContainer data-testid="voice-mode-container" />
      ) : (
        <Box
          data-testid="chat-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: {
              lg: 'calc(100dvh)',
              xs: 'calc(100dvh)',
            },
            [theme.breakpoints.down('lg')]: { paddingTop: '65px' },
            bgcolor: theme.palette.background.default,
            textAlign: 'center',
          }}
        >
          <Box
            data-testid="announcement-banners-container"
            sx={{
              position: 'fixed',
              zIndex: 10,
              padding: '0.5rem',
              overflow: 'auto',
              width: { xs: '100%', sm: '100%', md: '70%', lg: '70%' },
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#d1d5db', // light gray thumb
                borderRadius: '4px',
              },
            }}
          >
            <AnnouncementBanners data-testid="announcement-banners" />
          </Box>
          {/* <AddBannerButtons /> */}
          {!showChatLayout && !type && (
            <ChatGreeting data-testid="chat-greeting" />
          )}
          {type && isStreaming === false && (
            <ExploreLayout
              type={type}
              handleSuggestionClick={handleSendMessage}
              isStreaming={isStreaming}
              data-testid="explore-layout"
            />
          )}
          {showChatLayout && (
            <>
              {chatHistoryLoaded ? (
                <ChatHistory
                  chatId={chatId}
                  isStreaming={isStreaming}
                  streamingText={streamingText}
                  showTypingIndicator={showTypingIndicator}
                  setInput={setInput}
                  handleSendMessage={handleSendMessage}
                  data-testid="chat-history"
                />
              ) : (
                <Box
                  data-testid="chat-history-loading"
                  sx={{
                    overflow: 'hidden',
                    width: '100%',
                    padding: { sm: '0px 92px', xs: '0px 15px' },
                  }}
                >
                  <ChatHistoryLoader data-testid="chat-history-loader-1" />
                  <ChatHistoryLoader data-testid="chat-history-loader-2" />
                </Box>
              )}
            </>
          )}
          {!showChatLayout && !type && input.length <= 0 && (
            <ChatSuggestions handleSendMessage={handleSendMessage} />
          )}

          <ChatInputContainer
            input={input}
            handleInputChange={handleInputChange}
            handleKeyPress={handleKeyPress}
            listening={listening}
            startTranscription={startTranscription}
            stopTranscription={stopTranscription}
            handleSendMessage={handleSendMessage}
            handlePulseClick={handlePulseClick}
            isStreaming={isStreaming}
            showMaxCharTooltip={showMaxCharTooltip}
            isImpersonating={isStaffUser}
            data-testid="chat-input-container"
          />
        </Box>
      )}
    </Box>
  );
};

export default ChatPage;
